import { useEffect } from 'react';
import useEvent from '../app/useEvent';
import { useAuthStore } from '../context/AuthContext';
import queryParamsUtils from '../utils/queryParamsUtils';
import { useRotationStore } from '../context/RotationContext';
import { useEventStore } from '../context/EventContext';
import useApi from './useApi';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { getUtcTimeNow } from 'odfjell-date-time-utils/dateTimeUtils';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { deleteEventInList } = useEvent();
    const { state, postApiData } = useApi();
    const { signalRConnectionId, loggedInUser } = useAuthStore();
    const { rotation } = useRotationStore();
    const { createPortCallListQueryParam } = queryParamsUtils();
    const { setLastDeleteEventDate, setLastEventChangeDate } = useEventStore();
    const { deleteEventsForPortCallInList, deleteEventsForBerthCallInList, deleteEventsForCargoOperationInList } = useEvent();
    const { processApiEndpointError } = apiEndpointUtils();

    const isOfflineResponse = (result: any) => result?.isOfflineResponse && result.eventId;

    useEffect(() => {
        if (state.status === 'success' && (state.result === 1 || isOfflineResponse)) {
            if (state.endpoint === apiEndpoints['deleteEvent'].url) {
                const now = getUtcTimeNow();
                setLastDeleteEventDate(now);
                setLastEventChangeDate(now);
                deleteEventInList(state.additionalInfo.eventId);
            }
            if (state.endpoint === apiEndpoints['deleteEventsForPortCall'].url) {
                const now = getUtcTimeNow();
                setLastDeleteEventDate(now);
                setLastEventChangeDate(now);
                deleteEventsForPortCallInList(state.additionalInfo.portCallId, state.additionalInfo.allBerthCalls);
            }
            if (state.endpoint === apiEndpoints['deleteEventsForBerthCall'].url) {
                const now = getUtcTimeNow();
                setLastDeleteEventDate(now);
                setLastEventChangeDate(now);
                deleteEventsForBerthCallInList(state.additionalInfo.portCallId, state.additionalInfo.berthCallId, state.additionalInfo.allCargoOps);
            }
            if (state.endpoint === apiEndpoints['deleteEventsForCargoOperation'].url) {
                const now = getUtcTimeNow();
                setLastDeleteEventDate(now);
                setLastEventChangeDate(now);
                deleteEventsForCargoOperationInList(
                    state.additionalInfo.portCallId,
                    state.additionalInfo.berthCallId,
                    state.additionalInfo.cargoOperationId
                );
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    const deleteEvent = async (imoNumber: number, eventId: string): Promise<any> => {
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['deleteEvent'].url,
            queryParam: buildQueryParams({ imoNumber, portCallIdList, user: loggedInUser!.userName, eventId, connectionId: signalRConnectionId }),
            additionalInfo: { eventId: eventId },
        });

        return result;
    };

    const deleteEventsForPortCall = async (imoNumber: number, portCallId: number, allBerthCalls: boolean) => {
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['deleteEventsForPortCall'].url,
            queryParam: buildQueryParams({
                imoNumber,
                portCallIdList,
                user: loggedInUser!.userName,
                portCallId,
                allBerthCalls,
                connectionId: signalRConnectionId,
            }),
            additionalInfo: { portCallId, allBerthCalls },
        });

        return result;
    };

    const deleteEventsForBerthCall = async (imoNumber: number, portCallId: number, berthCallId: number, allCargoOps: boolean) => {
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['deleteEventsForBerthCall'].url,
            queryParam: buildQueryParams({
                imoNumber,
                portCallIdList,
                user: loggedInUser!.userName,
                portCallId,
                berthCallId,
                allCargoOps,
                connectionId: signalRConnectionId,
            }),
            additionalInfo: { portCallId, berthCallId, allCargoOps },
        });

        return result;
    };

    const deleteEventsForCargoOperation = async (imoNumber: number, portCallId: number, berthCallId: number, cargoOperationId: number) => {
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['deleteEventsForCargoOperation'].url,
            queryParam: buildQueryParams({
                imoNumber,
                portCallIdList,
                user: loggedInUser!.userName,
                portCallId,
                berthCallId,
                cargoOperationId,
                connectionId: signalRConnectionId,
            }),
            additionalInfo: { portCallId, berthCallId, cargoOperationId },
        });

        return result;
    };

    return { deleteEvent, deleteEventsForCargoOperation, deleteEventsForBerthCall, deleteEventsForPortCall };
};
